import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Browsers cap element height (Firefox ~17.9M px); past this the scroll position is scaled.
const MAX_SCROLL_HEIGHT = 15_000_000;

interface UseVirtualListOptions {
  count: number;
  /** Height of an unmeasured row in px; rows of exactly this height are never stored. */
  estimateSize: number;
  overscan?: number;
  /** Changing it scrolls to the top and drops measurements (new search, sort or config). */
  resetKey: string;
}

export interface VirtualItem {
  index: number;
  top: number;
}

export interface VisibleRange {
  start: number;
  end: number;
}

// Only rows that differ from the estimate are stored: offset(i) = i × estimate + their deltas before i, by binary search.
interface Measurements {
  indices: number[];
  prefixDeltas: number[];
}

const lowerBound = (values: number[], target: number) => {
  let low = 0;
  let high = values.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (values[mid] < target) low = mid + 1;
    else high = mid;
  }
  return low;
};

const buildMeasurements = (heights: Map<number, number>, estimateSize: number): Measurements => {
  const indices = [...heights.keys()].sort((a, b) => a - b);
  const prefixDeltas = [0];
  indices.forEach((index, position) => prefixDeltas.push(prefixDeltas[position] + (heights.get(index) ?? estimateSize) - estimateSize));
  return { indices, prefixDeltas };
};

const offsetOf = (index: number, estimateSize: number, { indices, prefixDeltas }: Measurements) =>
  index * estimateSize + prefixDeltas[lowerBound(indices, index)];

// Last index whose top is at or above `offset`.
const indexAt = (offset: number, count: number, estimateSize: number, measurements: Measurements) => {
  let low = 0;
  let high = count - 1;
  while (low < high) {
    const mid = (low + high + 1) >> 1;
    if (offsetOf(mid, estimateSize, measurements) <= offset) low = mid;
    else high = mid - 1;
  }
  return low;
};

const outerHeight = (element: Element) => {
  const { marginTop, marginBottom } = getComputedStyle(element);
  return element.getBoundingClientRect().height + parseFloat(marginTop) + parseFloat(marginBottom);
};

export const useVirtualList = ({ count, estimateSize, overscan = 6, resetKey }: UseVirtualListOptions) => {
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const heightsRef = useRef(new Map<number, number>());
  const [measurementsVersion, setMeasurementsVersion] = useState(0);
  const [viewport, setViewport] = useState({ scrollTop: 0, height: 0 });
  const scrollTopRef = useRef(0);

  const scrollRef = useCallback((node: HTMLElement | null) => setScrollElement(node), []);

  const measurements = useMemo(
    () => buildMeasurements(heightsRef.current, estimateSize),
    // measurementsVersion is the signal that heightsRef changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [measurementsVersion, estimateSize],
  );

  const virtualSize = count ? offsetOf(count, estimateSize, measurements) : 0;
  const scrollSize = Math.min(virtualSize, MAX_SCROLL_HEIGHT);
  // 1 for normal lists; > 1 only when the list is taller than the browser allows.
  const scale = scrollSize > viewport.height ? Math.max(1, (virtualSize - viewport.height) / (scrollSize - viewport.height)) : 1;
  const isScaled = scale > 1;
  const virtualTop = viewport.scrollTop * scale;

  const range = useMemo<VisibleRange>(() => {
    if (!count) return { start: 0, end: -1 };
    const first = indexAt(virtualTop, count, estimateSize, measurements);
    const last = indexAt(virtualTop + viewport.height, count, estimateSize, measurements);
    return { start: Math.max(0, first - overscan), end: Math.min(count - 1, last + overscan) };
  }, [count, estimateSize, measurements, overscan, virtualTop, viewport.height]);

  const items = useMemo(() => {
    const visible: VirtualItem[] = [];
    // Scaled lists place rows relative to the real scroll position; normal ones at their true offset.
    const shift = isScaled ? viewport.scrollTop - virtualTop : 0;
    for (let index = range.start; index <= range.end; index += 1) {
      visible.push({ index, top: offsetOf(index, estimateSize, measurements) + shift });
    }
    return visible;
  }, [range, estimateSize, measurements, isScaled, viewport.scrollTop, virtualTop]);

  // Scroll position lives in a ref; state only changes when the visible rows do (or every frame when scaled).
  const rangeRef = useRef(range);
  rangeRef.current = range;
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const layoutRef = useRef({ count, estimateSize, measurements, overscan, height: viewport.height });
  layoutRef.current = { count, estimateSize, measurements, overscan, height: viewport.height };

  useEffect(() => {
    if (!scrollElement) return;
    let frame = 0;
    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const scrollTop = scrollElement.scrollTop;
        scrollTopRef.current = scrollTop;
        const { count: total, estimateSize: estimate, measurements: measured, overscan: extra, height } = layoutRef.current;
        if (scaleRef.current > 1 || !total) {
          setViewport((current) => ({ ...current, scrollTop }));
          return;
        }
        const first = Math.max(0, indexAt(scrollTop, total, estimate, measured) - extra);
        const last = Math.min(total - 1, indexAt(scrollTop + height, total, estimate, measured) + extra);
        if (first !== rangeRef.current.start || last !== rangeRef.current.end) setViewport((current) => ({ ...current, scrollTop }));
      });
    };
    scrollElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      scrollElement.removeEventListener('scroll', handleScroll);
    };
  }, [scrollElement]);

  useEffect(() => {
    if (!scrollElement) return;
    const observer = new ResizeObserver(([entry]) => {
      const height = entry.contentRect.height;
      setViewport((current) => (current.height === height ? current : { scrollTop: scrollTopRef.current, height }));
    });
    observer.observe(scrollElement);
    return () => observer.disconnect();
  }, [scrollElement]);

  // One observer for every mounted row; each row says which index it is through data-index.
  const rowObserver = useMemo(
    () =>
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver((entries) => {
            let changed = false;
            entries.forEach(({ target }) => {
              const index = Number((target as HTMLElement).dataset.index);
              if (!Number.isInteger(index)) return;
              const height = outerHeight(target);
              const isEstimate = Math.abs(height - estimateSize) < 0.5;
              const previous = heightsRef.current.get(index);
              if (isEstimate ? previous === undefined : previous !== undefined && Math.abs(previous - height) < 0.5) return;
              if (isEstimate) heightsRef.current.delete(index);
              else heightsRef.current.set(index, height);
              changed = true;
            });
            if (changed) setMeasurementsVersion((version) => version + 1);
          }),
    [estimateSize],
  );

  // Observed rows by index. Unmounted rows must be unobserved, or the observer keeps detached nodes alive.
  const observedRowsRef = useRef(new Map<number, HTMLElement>());

  useEffect(() => {
    const observedRows = observedRowsRef.current;
    return () => {
      rowObserver?.disconnect();
      observedRows.clear();
    };
  }, [rowObserver]);

  useEffect(() => {
    observedRowsRef.current.forEach((node, index) => {
      if (node.isConnected) return;
      rowObserver?.unobserve(node);
      observedRowsRef.current.delete(index);
    });
  }, [range, rowObserver]);

  // Stable, so rows don't detach and reattach their ref on every render.
  const measureRef = useCallback(
    (node: HTMLElement | null) => {
      if (!node || !rowObserver) return;
      const index = Number(node.dataset.index);
      const previous = observedRowsRef.current.get(index);
      if (previous === node) return;
      if (previous) rowObserver.unobserve(previous);
      observedRowsRef.current.set(index, node);
      rowObserver.observe(node);
    },
    [rowObserver],
  );

  useLayoutEffect(() => {
    heightsRef.current.clear();
    setMeasurementsVersion((version) => version + 1);
    scrollTopRef.current = 0;
    setViewport((current) => (current.scrollTop === 0 ? current : { ...current, scrollTop: 0 }));
    if (scrollElement) scrollElement.scrollTop = 0;
    // Only a new resetKey restarts the list; the element attaching is handled by the scroll effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Brings a row into view, top-aligned, only when it isn't fully visible already.
  const scrollToIndex = useCallback(
    (index: number) => {
      if (!scrollElement) return;
      const { estimateSize: estimate, measurements: measured, height } = layoutRef.current;
      const top = offsetOf(index, estimate, measured) / scaleRef.current;
      const current = scrollElement.scrollTop;
      if (top >= current && top + estimate <= current + height) return;
      scrollElement.scrollTop = top;
    },
    [scrollElement],
  );

  return { scrollRef, measureRef, items, range, scrollSize, scrollToIndex };
};
